import {AuthRepository} from "@web-libs/features/auth/main/domain/auth.repository";
import {HttpClient} from "@web-libs/core/http";
import {
    AnonymousRegistration,
    AuthTokens,
    NormalRegistration,
    PasswordResetPayload
} from "@web-libs/features/auth/main/domain/auth.types";
import {OidcResponse} from "@web-libs/features/auth/main/api/oidc.types";
import {UnexpectedDomainError} from "@web-libs/core/shared-domain/shared-domain.errors";
import {RequestError, UnauthorizedError} from "@web-libs/core/http/api/http.errors";
import {CredentialsError, EmailInUseError, UserNameInUseError} from "@web-libs/features/auth/main/domain/auth.errors";
import {parseAnonymousRegisterDTO, parseRegisterDTO} from "@web-libs/features/auth/main/api/auth.types";
import {throwErrorWithMessage} from "@web-libs/core/errors";
import {HttpConcern} from "@web-libs/core/http/infrastructure/client-side/use-http.concern";

export const createAuthRepository = (http:HttpConcern, oidcConfigPath: string, clientId:string) : AuthRepository => {
    return {
        login: async (code):Promise<AuthTokens> => {
            const oidcConfig = await getOidcConfig(http, oidcConfigPath)
            const formData = buildFormData(code, clientId)
            return await fetchToken(http, oidcConfig.token_endpoint, formData)
        },
        requestLogin: async (payload):Promise<void> => {
          const oidcConfig = await getOidcConfig(http, oidcConfigPath)
            const params:Record<string, string> = {
                response_type: "code",
                client_id: clientId,
                redirect_uri: payload.redirectUrl,
                reset_uri: payload.resetUrl,
                logo: payload.logoUrl,
                primaryColor: payload.primaryColor,
                registerUrl: payload.registerUrl,
                scope: "openid profile email",
            }
            if(http.tenantId){
                params.tenantId = http.tenantId
            }
            if(payload.stage){
                params.stage = payload.stage
            }
            const requestUrl = addQueryParams(oidcConfig.authorization_endpoint, params)
            localStorage.setItem("redirectUrl", payload.redirectUrl)
            //navigate to authUrl
            window.location.href = requestUrl
        },
        refreshToken: async (refreshToken: string):Promise<string> => {
            const oidcConfig = await getOidcConfig(http, oidcConfigPath)
            const formData = buildRefreshFormData(refreshToken, clientId)
            const response =  await http.postForm<AuthTokens>(oidcConfig.token_endpoint, formData, {skipTenantId:true})
            return response.access_token
        },
        register: async (registration: NormalRegistration): Promise<boolean> => {
            const response = await http.post("/public/v2/users/register", parseRegisterDTO(registration)).then(
                (response: any) => {
                    if(response.code == 0 && response.status == "EMAIL_IN_USE")  throw new EmailInUseError()
                    return response
                }
            )
            return !!response
        },
        registerAnonymous: async (registration: AnonymousRegistration): Promise<boolean> => {
            const response = await http.post("/public/v2/users/register/anonymous", parseAnonymousRegisterDTO(registration)).then(
                (response: any) => {
                    if(response.code == 0 && response.status == "USERNAME_IN_USE")  throw new UserNameInUseError()
                    return response
                }
            )
            return !!response
        },
        checkEmailUsed: async (email: string) => {
            const response = await http.get("/public/users/emailIsUsed", {queryParams: {email: email}})
            return !!response
        },
        checkUsernameUsed: async (username: string) => {
            const response = await http.get("/public/users/usernameIsUsed", {queryParams: {username: username}})
            return !!response
        },

        resetPassword: async (payload:PasswordResetPayload)=> {
            if(payload.passwordCode === "bypass"){
                return new Promise((resolve) => {
                       setTimeout(() => {
                            resolve(true)
                        }, 3000)
                })
            }
            const url = "/public/users/changePasswordByPWCode/" + payload.userId + "/" + payload.passwordCode
            const response = await http.put(url, undefined, {
                queryParams:{
                    pw: payload.newPassword,
                }
            })
            return !!response
        },
        logout: async (redirectUrl:string) => {
            const oidcConfig = await getOidcConfig(http, oidcConfigPath)
            const params:Record<string, string> = {
                post_logout_redirect_uri: redirectUrl,
                client_id: clientId,
            }
            window.location.href = addQueryParams(oidcConfig.end_session_endpoint, params)
        }
    }
}

const fetchToken = async (http:HttpClient, tokenEndpoint:string, formData: URLSearchParams) => {
    try{
        return await http.postForm<AuthTokens>(tokenEndpoint, formData, {skipTenantId:true})
    } catch (error) {
        if(error instanceof UnauthorizedError){
            throw new CredentialsError()
        } else {
            throw UnexpectedDomainError.fromError(error)
        }
    }
}

const buildRefreshFormData = (refreshToken: string, clientId: string) => {
    const formData = new URLSearchParams();
    formData.append('grant_type', 'refresh_token');
    formData.append('refresh_token', refreshToken);
    formData.append('client_id', clientId);
    return formData
}

const buildFormData = (code:string, clientId: string) => {
    const redirectUrl = localStorage.getItem("redirectUrl")
    const formData = new URLSearchParams();
    formData.append('grant_type', 'authorization_code');
    formData.append('code', code);
    formData.append('client_id', clientId);
    formData.append('redirect_uri', redirectUrl || "");
    return formData
}

const getOidcConfig = async (http:HttpClient, oidcConfigPath: string) => {
    return await http.get<OidcResponse>(oidcConfigPath,{skipTenantId:true}).catch(
        (error) => {
            throw new UnexpectedDomainError("Failed to fetch oidc config." + error.message)
        }
    )
}

const addQueryParams = (url:string, params:Record<string, string>) => {
    const urlWithParams = new URL(url)
    Object.entries(params).forEach(([key, value]) => {
        urlWithParams.searchParams.append(key, value)
    })
    return urlWithParams.toString()
}
